import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { API_BASE } from '../config/api';
import { resolveSession } from '../lib/session';
import { useSessionUser } from '../SessionContext';
import './Reports.css';

const PAGE_SIZE = 100;

const emptyTotals = {
  totalEvents: 0,
  uniqueAgents: 0,
  totalDurationSeconds: 0,
  avgDurationSeconds: 0,
  loginEvents: 0,
  logoutEvents: 0,
  readyEvents: 0,
  notReadyEvents: 0,
  onCallEvents: 0,
};

const DEFAULT_SQL = `SELECT
  eventtime,
  agentid,
  status,
  reason,
  inqueue,
  direction,
  cmsuserid,
  cmsusername,
  prevstatus,
  prevdurationseconds
FROM agentstate
WHERE eventtime >= :start_date::date
  AND eventtime < (:end_date::date + INTERVAL '1 day')
ORDER BY eventtime ASC, agentid ASC;`;

const summaryColumns = [
  { key: 'agentId', label: 'Agent ID' },
  { key: 'cmsUserName', label: 'Agent Name' },
  { key: 'state', label: 'State' },
  { key: 'eventCount', label: 'Events' },
  { key: 'durationCount', label: 'Completed Durations' },
  { key: 'totalDurationSeconds', label: 'Total Duration', type: 'time' },
  { key: 'avgDurationSeconds', label: 'Avg Duration', type: 'time' },
  { key: 'lastEventTime', label: 'Last Event', type: 'datetime' },
];

const detailColumns = [
  { key: 'date', label: 'Date', type: 'date' },
  { key: 'time', label: 'Time' },
  { key: 'agentId', label: 'Agent ID' },
  { key: 'cmsUserName', label: 'Agent Name' },
  { key: 'status', label: 'Status' },
  { key: 'reason', label: 'Reason' },
  { key: 'inQueue', label: 'In Queue' },
  { key: 'direction', label: 'Direction' },
  { key: 'prevStatus', label: 'Prev Status' },
  { key: 'prevDurationSeconds', label: 'Prev Duration', type: 'time' },
];

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const todayIso = () => toIsoDate(new Date());

const monthStartIso = () => {
  const date = new Date();
  date.setDate(1);
  return toIsoDate(date);
};

const normalizeText = (value) => String(value ?? '').trim();

const readNumber = (source, keys, fallback = 0) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return Number(value) || fallback;
    }
  }
  return fallback;
};

const formatDate = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const [year, month, day] = text.slice(0, 10).split('-');
  if (!year || !month || !day) return text;
  return `${Number(month)}/${Number(day)}/${year}`;
};

const formatDateTime = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const date = new Date(text);
  if (!Number.isNaN(date.getTime())) {
    return `${formatDate(date.toISOString().slice(0, 10))} ${date.toLocaleTimeString('en-US', { hour12: false })}`;
  }
  return text;
};

const formatNumber = (value) => Math.round(Number(value) || 0).toLocaleString();

const formatHms = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remSeconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remSeconds).padStart(2, '0')}`;
};

const formatCell = (row, column) => {
  const value = row?.[column.key] ?? '';
  if (column.type === 'date') return formatDate(value);
  if (column.type === 'datetime') return formatDateTime(value);
  if (column.type === 'time') return formatHms(value);
  if (typeof value === 'number') return formatNumber(value);
  return value;
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const normalizeTotals = (source = {}) => ({
  totalEvents: readNumber(source, ['totalEvents', 'total_events', 'TotalEvents']),
  uniqueAgents: readNumber(source, ['uniqueAgents', 'unique_agents', 'UniqueAgents']),
  totalDurationSeconds: readNumber(source, ['totalDurationSeconds', 'total_duration_seconds', 'TotalDurationSeconds']),
  avgDurationSeconds: readNumber(source, ['avgDurationSeconds', 'avg_duration_seconds', 'AvgDurationSeconds']),
  loginEvents: readNumber(source, ['loginEvents', 'login_events', 'LoginEvents']),
  logoutEvents: readNumber(source, ['logoutEvents', 'logout_events', 'LogoutEvents']),
  readyEvents: readNumber(source, ['readyEvents', 'ready_events', 'ReadyEvents']),
  notReadyEvents: readNumber(source, ['notReadyEvents', 'not_ready_events', 'NotReadyEvents']),
  onCallEvents: readNumber(source, ['onCallEvents', 'on_call_events', 'OnCallEvents']),
});

const normalizeSummaryRow = (source = {}) => ({
  agentId: normalizeText(source.agentId ?? source.agent_id ?? source.AgentId),
  cmsUserName: normalizeText(source.cmsUserName ?? source.cms_user_name ?? source.CmsUserName),
  state: normalizeText(source.state ?? source.State),
  eventCount: readNumber(source, ['eventCount', 'event_count', 'EventCount']),
  durationCount: readNumber(source, ['durationCount', 'duration_count', 'DurationCount']),
  totalDurationSeconds: readNumber(source, ['totalDurationSeconds', 'total_duration_seconds', 'TotalDurationSeconds']),
  avgDurationSeconds: readNumber(source, ['avgDurationSeconds', 'avg_duration_seconds', 'AvgDurationSeconds']),
  lastEventTime: normalizeText(source.lastEventTime ?? source.last_event_time ?? source.LastEventTime),
});

const normalizeDetailRow = (source = {}) => ({
  date: normalizeText(source.date ?? source.reportDate ?? source.report_date),
  time: normalizeText(source.time ?? source.reportTime ?? source.report_time),
  eventTime: normalizeText(source.eventTime ?? source.event_time ?? source.EventTime),
  agentId: normalizeText(source.agentId ?? source.agent_id ?? source.AgentId),
  cmsUserId: normalizeText(source.cmsUserId ?? source.cms_user_id ?? source.CmsUserId),
  cmsUserName: normalizeText(source.cmsUserName ?? source.cms_user_name ?? source.CmsUserName),
  status: normalizeText(source.status ?? source.Status),
  reason: normalizeText(source.reason ?? source.Reason),
  inQueue: normalizeText(source.inQueue ?? source.in_queue ?? source.InQueue),
  direction: normalizeText(source.direction ?? source.Direction),
  prevStatus: normalizeText(source.prevStatus ?? source.prev_status ?? source.PrevStatus),
  prevDurationSeconds: readNumber(source, ['prevDurationSeconds', 'prev_duration_seconds', 'PrevDurationSeconds']),
});

const buildReportPath = (filters, page = 1) => {
  const params = new URLSearchParams();
  params.set('startDate', filters.startDate);
  params.set('endDate', filters.endDate);
  params.set('page', String(page));
  params.set('perPage', String(PAGE_SIZE));
  if (filters.agent) params.set('agent', filters.agent);
  if (filters.status) params.set('status', filters.status);
  if (filters.direction) params.set('direction', filters.direction);
  return `/reports/agent-state-report?${params.toString()}`;
};

const fetchJson = async (path) => {
  const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok || json?.ok === false || json?.success === false) {
    const message = json?.error || json?.message || text || `Request failed: ${response.status}`;
    if (response.status === 404 && String(message).toLowerCase().includes('route')) {
      throw new Error('Laravel report API route missing. Register GET /api/reports/agent-state-report in routes/api.php.');
    }
    throw new Error(message);
  }
  return json;
};

const AgentStateReport = () => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const [filters, setFilters] = useState({
    startDate: monthStartIso(),
    endDate: todayIso(),
    agent: '',
    status: '',
    direction: '',
  });
  const [report, setReport] = useState({
    totals: emptyTotals,
    summaryRows: [],
    detail: { rows: [], allRows: [], page: 1, perPage: PAGE_SIZE, totalRows: 0, totalPages: 1 },
    reportInterval: '',
    source: 'agentstate',
    dataIssues: [],
    sqlOutput: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = useCallback(async (nextFilters = filters, page = 1) => {
    if (!nextFilters.startDate || !nextFilters.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }
    if (nextFilters.startDate > nextFilters.endDate) {
      setError('Start Date cannot be after End Date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const json = await fetchJson(buildReportPath(nextFilters, page));
      const root = json?.data || json || {};
      const detail = root.detail || {};
      const rows = Array.isArray(detail.rows) ? detail.rows : [];
      const allRows = Array.isArray(detail.allRows) ? detail.allRows : rows;

      setReport({
        totals: normalizeTotals(root.totals || {}),
        summaryRows: Array.isArray(root.summaryRows) ? root.summaryRows.map(normalizeSummaryRow) : [],
        detail: {
          rows: rows.map(normalizeDetailRow),
          allRows: allRows.map(normalizeDetailRow),
          page: Number(detail.page || page),
          perPage: Number(detail.perPage || PAGE_SIZE),
          totalRows: Number(detail.totalRows || allRows.length || rows.length),
          totalPages: Number(detail.totalPages || 1),
        },
        reportInterval: root.reportInterval || '',
        source: normalizeText(root.source || 'agentstate'),
        dataIssues: Array.isArray(root.dataIssues) ? root.dataIssues : [],
        sqlOutput: root.sqlOutput || '',
      });
    } catch (err) {
      setReport((prev) => ({
        ...prev,
        totals: emptyTotals,
        summaryRows: [],
        detail: { rows: [], allRows: [], page: 1, perPage: PAGE_SIZE, totalRows: 0, totalPages: 1 },
      }));
      setError(err?.message || 'Failed to load Agent State Report.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    document.body.setAttribute('data-app', 'reports');
    return () => document.body.removeAttribute('data-app');
  }, []);

  useEffect(() => {
    loadReport(filters, 1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value.trimStart() }));
  };

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const summaryRows = [
      ['Agent State Report'],
      ['Report Interval', report.reportInterval || `${filters.startDate} - ${filters.endDate}`],
      ['Data Source', report.source],
      ['Agent Filter', filters.agent || 'All'],
      ['Status Filter', filters.status || 'All'],
      ['Direction Filter', filters.direction || 'All'],
      [],
      ['Totals'],
      ['Total Events', report.totals.totalEvents],
      ['Unique Agents', report.totals.uniqueAgents],
      ['Total Duration', formatHms(report.totals.totalDurationSeconds)],
      ['Average Duration', formatHms(report.totals.avgDurationSeconds)],
      ['Ready Events', report.totals.readyEvents],
      ['Not Ready Events', report.totals.notReadyEvents],
      ['On Call Events', report.totals.onCallEvents],
      [],
      ['Summary'],
      summaryColumns.map((column) => column.label),
      ...report.summaryRows.map((row) => summaryColumns.map((column) => formatCell(row, column))),
    ];
    const detailRows = [
      detailColumns.map((column) => column.label),
      ...(report.detail?.allRows || []).map((row) => detailColumns.map((column) => formatCell(row, column))),
    ];

    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(detailRows), 'Record Detail');
    XLSX.writeFile(workbook, `agent-state-report-${filters.startDate}-${filters.endDate}.xlsx`);
  };

  const currentPage = Number(report.detail?.page || 1);
  const totalPages = Number(report.detail?.totalPages || 1);
  const totalRows = Number(report.detail?.totalRows || 0);

  return (
    <div className="cdn-report-shell agent-state-report-shell">
      <header className="cdn-report-topbar no-print">
        <div>
          <strong>SLACC</strong>
          <span>Sindh Legal Advisory Call Center</span>
        </div>
        <div>
          <h1>Agent State Report</h1>
          <p>Database report</p>
        </div>
        <div className="cdn-report-user-actions">
          <span>{username ? `${username}${roleName ? ` - ${roleName}` : ''}` : 'Report Module'}</span>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports')}>CDN Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/dnis-statistics')}>DNIS Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/daily-call-report')}>Daily Call Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-login-logout-report')}>Login/Logout</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-performance-report')}>Performance</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/backoffice')}>Back Office</button>
          <button type="button" className="cdn-btn danger" onClick={() => navigate('/logout')}>Logout</button>
        </div>
      </header>

      <section className="cdn-filter-panel no-print">
        <div className="agent-state-filter-grid">
          <label><span>Start Date</span><input type="date" name="startDate" value={filters.startDate} onChange={updateField} /></label>
          <label><span>End Date</span><input type="date" name="endDate" value={filters.endDate} onChange={updateField} /></label>
          <label><span>Agent</span><input type="text" name="agent" value={filters.agent} onChange={updateField} placeholder="ID or name" /></label>
          <label><span>Status</span><input type="text" name="status" value={filters.status} onChange={updateField} placeholder="All status" /></label>
          <label>
            <span>Direction</span>
            <select name="direction" value={filters.direction} onChange={updateField}>
              <option value="">All Direction</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </label>
          <div className="cdn-filter-actions">
            <button type="button" className="cdn-btn primary" onClick={() => loadReport(filters, 1)} disabled={loading}>
              {loading ? 'Loading' : 'Show'}
            </button>
            <button type="button" className="cdn-btn success" onClick={exportExcel} disabled={!report.detail?.allRows?.length}>Excel</button>
            <button type="button" className="cdn-btn soft" onClick={() => window.print()} disabled={!report.detail?.allRows?.length}>PDF</button>
          </div>
        </div>
        {error ? <div className="cdn-alert">{error}</div> : null}
        {report.dataIssues?.length ? (
          <div className="dnis-data-issues">
            {report.dataIssues.map((issue) => <p key={issue}>{issue}</p>)}
          </div>
        ) : null}
      </section>

      <main className="cdn-report-paper agent-state-report-paper">
        <section className="cdn-report-heading">
          <h2>Agent State Report</h2>
          <p><strong>Report Interval:</strong> {report.reportInterval || `${filters.startDate} 00:00:00 - ${filters.endDate} 23:59:59`}</p>
          <p><strong>Agent / Status / Direction:</strong> {filters.agent || 'All'} / {filters.status || 'All'} / {filters.direction || 'All'}</p>
        </section>

        <section className="daily-call-kpi-grid agent-state-kpi-grid">
          <article><span>Total Events</span><strong>{formatNumber(report.totals.totalEvents)}</strong></article>
          <article><span>Unique Agents</span><strong>{formatNumber(report.totals.uniqueAgents)}</strong></article>
          <article><span>Total Duration</span><strong>{formatHms(report.totals.totalDurationSeconds)}</strong></article>
          <article><span>Avg Duration</span><strong>{formatHms(report.totals.avgDurationSeconds)}</strong></article>
          <article><span>Ready Events</span><strong>{formatNumber(report.totals.readyEvents)}</strong></article>
          <article><span>Not Ready</span><strong>{formatNumber(report.totals.notReadyEvents)}</strong></article>
        </section>

        <section className="daily-call-table-section">
          <h3 className="daily-call-section-title">Summary</h3>
          <table className="daily-call-report-table agent-state-summary-table">
            <thead>
              <tr>
                {summaryColumns.map((column) => <th key={column.key}>{column.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {report.summaryRows.length ? report.summaryRows.map((row, index) => (
                <tr key={`${row.agentId}-${row.state}-${index}`}>
                  {summaryColumns.map((column) => <td key={column.key}>{formatCell(row, column)}</td>)}
                </tr>
              )) : (
                <tr>
                  <td colSpan={summaryColumns.length} className="cdn-empty">
                    {loading ? 'Loading summary...' : 'No summary data found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="daily-call-table-section">
          <h3 className="daily-call-section-title">Record Detail</h3>
          <table className="daily-call-report-table agent-state-detail-table">
            <thead>
              <tr>
                {detailColumns.map((column) => <th key={column.key}>{column.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {report.detail?.rows?.length ? report.detail.rows.map((row, index) => (
                <tr key={`${row.agentId || 'agent'}-${row.eventTime || row.time}-${index}`}>
                  {detailColumns.map((column) => <td key={column.key}>{formatCell(row, column)}</td>)}
                </tr>
              )) : (
                <tr>
                  <td colSpan={detailColumns.length} className="cdn-empty">
                    {loading ? 'Loading record detail...' : 'No record detail found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </main>

      <section className="cdn-pager no-print">
        <button type="button" className="cdn-btn soft" disabled={loading || currentPage <= 1} onClick={() => loadReport(filters, currentPage - 1)}>Previous</button>
        <span>Page {currentPage} of {totalPages} - {formatNumber(totalRows)} detail records</span>
        <button type="button" className="cdn-btn soft" disabled={loading || currentPage >= totalPages} onClick={() => loadReport(filters, currentPage + 1)}>Next</button>
      </section>

      <section className="cdn-sql-panel no-print">
        <details>
          <summary>Database SQL Output</summary>
          <pre>{report.sqlOutput || DEFAULT_SQL}</pre>
        </details>
      </section>
    </div>
  );
};

export default AgentStateReport;
